<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgentConsentsAPI from 'dashboard/api/agentConsents';
import AgentVoiceProfilesAPI from 'dashboard/api/agentVoiceProfiles';
import { uploadFile } from 'dashboard/helper/uploadHelper';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const profile = ref(null);
const provider = ref('xtts_v2');
const language = ref('pt-BR');
const referenceTranscript = ref('');
const pendingFiles = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const isAcceptingConsent = ref(false);

const isReady = computed(() => profile.value?.status === 'ready');
const referenceAudios = computed(() => profile.value?.reference_audios || []);
const consented = computed(() => Boolean(profile.value?.consented));
const selectedProviderDescription = computed(() =>
  provider.value === 'qwen3_tts'
    ? t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.QWEN_HINT')
    : t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.XTTS_HINT')
);

const loadProfile = async () => {
  isLoading.value = true;
  try {
    const { data } = await AgentVoiceProfilesAPI.get(props.agentId);
    profile.value = data;
    provider.value = data.provider || 'xtts_v2';
    language.value = data.language || 'pt-BR';
    referenceTranscript.value = data.reference_transcript || '';
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const selectFiles = event => {
  pendingFiles.value = Array.from(event.target.files || []);
};

const acceptConsent = async () => {
  isAcceptingConsent.value = true;
  try {
    await AgentConsentsAPI.accept(
      'voice_clone',
      { source: 'agent_voice_profile', accepted_by: 'account_administrator' },
      { user_id: props.agentId }
    );
    await loadProfile();
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.CONSENT_SUCCESS'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.CONSENT_ERROR'));
  } finally {
    isAcceptingConsent.value = false;
  }
};

const saveProfile = async () => {
  if (pendingFiles.value.length && !consented.value) {
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.CONSENT_REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    const referenceAudioBlobIds = await Promise.all(
      pendingFiles.value.map(file => uploadFile(file))
    );
    const payload = {
      provider: provider.value,
      language: language.value,
      reference_transcript: referenceTranscript.value.trim() || null,
      reference_audio_blob_ids: referenceAudioBlobIds.map(file => file.blobId),
    };
    const response = profile.value?.id
      ? await AgentVoiceProfilesAPI.update(props.agentId, payload)
      : await AgentVoiceProfilesAPI.create(props.agentId, payload);
    profile.value = response.data;
    pendingFiles.value = [];
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const removeReferenceAudio = async attachmentId => {
  isSaving.value = true;
  try {
    const { data } = await AgentVoiceProfilesAPI.removeReferenceAudio(
      props.agentId,
      attachmentId
    );
    profile.value = data;
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.REMOVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadProfile);
</script>

<template>
  <section
    class="w-full rounded-lais border border-[#d9d2e6] bg-[#fcfbfe] p-4 sm:p-5"
  >
    <div
      class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
    >
      <div>
        <h3 class="text-[14px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.TITLE') }}
        </h3>
        <p class="mt-1 text-[12px] leading-5 text-[#667085]">
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.DESCRIPTION') }}
        </p>
      </div>
      <span
        class="inline-flex w-fit rounded-full px-2.5 py-1 text-[11px] font-semibold"
        :class="
          isReady
            ? 'bg-[#e8f7ed] text-[#16743c]'
            : 'bg-[#f3eff8] text-[#67548b]'
        "
      >
        {{
          isReady
            ? $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.STATUS_READY')
            : $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.STATUS_CONFIGURE')
        }}
      </span>
    </div>

    <div v-if="isLoading" class="mt-4 text-[13px] text-[#667085]">
      {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.LOADING') }}
    </div>

    <div v-else class="mt-4 grid gap-4">
      <div class="grid gap-4 md:grid-cols-2">
        <label class="block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.PROVIDER') }}
          <select
            v-model="provider"
            class="mt-2 h-11 w-full rounded-lais border border-[#d9d2e6] bg-white px-3 text-[14px] font-normal outline-none focus:border-[#49317a]"
          >
            <option value="xtts_v2">
              {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.PROVIDERS.XTTS') }}
            </option>
            <option value="qwen3_tts">
              {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.PROVIDERS.QWEN') }}
            </option>
          </select>
        </label>

        <label class="block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.LANGUAGE') }}
          <select
            v-model="language"
            class="mt-2 h-11 w-full rounded-lais border border-[#d9d2e6] bg-white px-3 text-[14px] font-normal outline-none focus:border-[#49317a]"
          >
            <option value="pt-BR">
              {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.LANGUAGES.PT_BR') }}
            </option>
            <option value="en">
              {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.LANGUAGES.EN') }}
            </option>
            <option value="es">
              {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.LANGUAGES.ES') }}
            </option>
          </select>
        </label>
      </div>

      <p
        class="rounded-lais bg-[#f3eff8] px-3 py-2 text-[12px] leading-5 text-[#49317a]"
      >
        {{ selectedProviderDescription }}
      </p>

      <label class="block text-[13px] font-semibold text-[#1a1a1a]">
        {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.REFERENCE_AUDIO') }}
        <input
          class="mt-2 block w-full text-[13px] font-normal text-[#667085] file:mr-3 file:rounded-lais file:border-0 file:bg-[#ede8f6] file:px-3 file:py-2 file:text-[12px] file:font-semibold file:text-[#49317a]"
          type="file"
          accept="audio/aac,audio/flac,audio/m4a,audio/mp4,audio/mpeg,audio/ogg,audio/opus,audio/wav,audio/webm,.aac,.flac,.m4a,.mp3,.ogg,.opus,.wav,.webm"
          multiple
          @change="selectFiles"
        />
      </label>
      <p v-if="pendingFiles.length" class="text-[12px] text-[#667085]">
        {{
          $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.PENDING_FILES', {
            count: pendingFiles.length,
          })
        }}
      </p>

      <label class="block text-[13px] font-semibold text-[#1a1a1a]">
        {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.TRANSCRIPT') }}
        <textarea
          v-model="referenceTranscript"
          rows="3"
          class="mt-2 w-full rounded-lais border border-[#d9d2e6] bg-white px-3 py-2 text-[14px] font-normal outline-none focus:border-[#49317a]"
          :placeholder="
            $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.TRANSCRIPT_PLACEHOLDER')
          "
        />
      </label>

      <div class="rounded-lais border border-[#e8e0f0] bg-white p-3">
        <p class="text-[12px] leading-5 text-[#475467]">
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.CONSENT_DESCRIPTION') }}
        </p>
        <button
          v-if="!consented"
          type="button"
          class="mt-3 inline-flex h-9 items-center rounded-lais bg-[#49317a] px-3 text-[12px] font-semibold text-white disabled:opacity-60"
          :disabled="isAcceptingConsent"
          @click="acceptConsent"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.ACCEPT_CONSENT') }}
        </button>
        <span
          v-else
          class="mt-3 inline-flex text-[12px] font-semibold text-[#16743c]"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.CONSENT_ACCEPTED') }}
        </span>
      </div>

      <ul
        v-if="referenceAudios.length"
        class="divide-y divide-[#eee5d6] rounded-lais border border-[#e8e0f0] bg-white"
      >
        <li
          v-for="audio in referenceAudios"
          :key="audio.id"
          class="flex items-center justify-between gap-3 px-3 py-2.5"
        >
          <span class="min-w-0 truncate text-[12px] text-[#344054]">{{
            audio.filename
          }}</span>
          <button
            type="button"
            class="text-[12px] font-semibold text-[#b42318] disabled:opacity-60"
            :disabled="isSaving"
            @click="removeReferenceAudio(audio.id)"
          >
            {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.REMOVE') }}
          </button>
        </li>
      </ul>

      <div class="flex justify-end">
        <button
          type="button"
          class="inline-flex h-10 items-center rounded-lais bg-[#49317a] px-4 text-[13px] font-semibold text-white disabled:opacity-60"
          :disabled="isSaving"
          @click="saveProfile"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.SAVE') }}
        </button>
      </div>
    </div>
  </section>
</template>
